import * as React from "react";
import { useLocation } from "react-router-dom";

import { isPostHogActive, posthog } from "@/instrument";

/**
 * Renderless companion to `PostHogIdentitySync`. posthog-js only fetches feature flags on load,
 * `identify`, and `group` change. In an installed PWA a session can stay open for days and the user
 * almost never reloads, so a flag flip (or a scheduled change) would otherwise never reach them.
 *
 * We actively re-pull flags on the moments that matter for a PWA:
 *   - route change,
 *   - the app regaining focus / becoming visible (returning to the installed app),
 *   - a light safety-net poll while the tab is visible.
 *
 * All triggers are throttled so rapid navigation / focus churn can't spam the /flags endpoint, and
 * everything is a no-op when PostHog isn't initialised (local dev), where the override + fail-safe
 * defaults apply instead.
 */
const MIN_RELOAD_INTERVAL_MS = 20_000;
const VISIBLE_POLL_INTERVAL_MS = 180_000;

export function PostHogFlagRefresh(): null {
  const location = useLocation();
  // Seed with mount time so the initial route effect doesn't re-pull flags that `init` just loaded.
  const lastReloadRef = React.useRef(Date.now());

  const reloadFlags = React.useCallback(() => {
    if (!isPostHogActive) return;
    const now = Date.now();
    if (now - lastReloadRef.current < MIN_RELOAD_INTERVAL_MS) return;
    lastReloadRef.current = now;
    posthog.reloadFeatureFlags();
  }, []);

  // Navigated to a new route.
  React.useEffect(() => {
    reloadFlags();
  }, [location.pathname, reloadFlags]);

  // App regained focus / became visible — the key trigger for long-lived PWA sessions.
  React.useEffect(() => {
    if (!isPostHogActive) return;
    const onFocus = () => reloadFlags();
    const onVisibility = () => {
      if (document.visibilityState === "visible") reloadFlags();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reloadFlags]);

  // Safety-net poll, only while the tab is actually visible (no background churn).
  React.useEffect(() => {
    if (!isPostHogActive) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") reloadFlags();
    }, VISIBLE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [reloadFlags]);

  return null;
}
